import React, { useCallback, useState } from 'react';
import { ExerciseLocation } from 'utils/draftActivityPlan';
import {
  EditExerciseModal,
  EditExerciseModalProps
} from '../EditExerciseModal';
import { ValidationFn } from '../ExerciseInput';

interface ChronicExerciseValues {
  reps: number;
  holdTime: number;
  pointsPerRep: number;
  switchLimbTime: null | number;
}

export interface ChronicEditExerciseModalProps
  extends Omit<EditExerciseModalProps, 'inputs'> {
  initialValues: null | ChronicExerciseValues;
}

const validations: ValidationFn[] = [
  value => ({ isValid: value > 0, reason: 'must be greater than 0' }),
  value => ({ isValid: value < 100, reason: 'must be less than 100' })
];

const valuesToInputs = ({
  reps,
  holdTime,
  pointsPerRep,
  switchLimbTime
}: ChronicExerciseValues): EditExerciseModalProps['inputs'] => ({
  reps: {
    label: 'Reps',
    initialValue: reps,
    validations
  },
  holdTime: {
    label: 'Hold Time',
    unit: 'sec',
    initialValue: holdTime / 1000,
    validations
  },
  pointsPerRep: {
    label: 'Points per rep',
    unit: 'points',
    initialValue: pointsPerRep,
    validations
  },
  // When switchLimbTime is null, we have a one-sided exercise
  ...(switchLimbTime !== null
    ? {
        switchLimbTime: {
          label: 'Switch Limb Time',
          unit: 'sec',
          initialValue: switchLimbTime / 1000,
          validations
        }
      }
    : null)
});

export const ChronicEditExerciseModal = ({
  name,
  open,
  initialValues,
  onSubmit,
  onCancel
}: ChronicEditExerciseModalProps): JSX.Element => (
  <EditExerciseModal
    name={name}
    open={open}
    onCancel={onCancel}
    onSubmit={values => {
      onSubmit({
        ...values,
        ...(values.holdTime ? { holdTime: values.holdTime * 1000 } : {}),
        ...(values.switchLimbTime
          ? { switchLimbTime: values.switchLimbTime * 1000 }
          : {})
      });
    }}
    inputs={initialValues !== null ? valuesToInputs(initialValues) : {}}
  />
);

export interface ExerciseData {
  name: string;
  reps: number;
  holdTime: number;
  pointsPerRep: number;
  switchLimbTime: null | number;
}

interface EditExerciseModalState {
  name: string;
  location: ExerciseLocation;
  initialValues: Omit<ExerciseData, 'name'>;
}

export interface UseEditExerciseModal {
  open: (location: ExerciseLocation, exercise: ExerciseData) => void;
  close: () => void;
  modal: EditExerciseModalState | null;
}

/**
 * Create an API/state that can be used to facilitate opening and closing
 * a single ChronicEditExerciseModal instance across many exercises.
 */
export const useChronicEditExerciseModal = (): UseEditExerciseModal => {
  const [modal, setModal] = useState<null | EditExerciseModalState>(null);

  return {
    open: useCallback(
      (location: ExerciseLocation, exercise: ExerciseData) =>
        setModal({
          name: exercise.name,
          location,
          initialValues: {
            reps: exercise.reps,
            holdTime: exercise.holdTime,
            pointsPerRep: exercise.pointsPerRep,
            switchLimbTime: exercise.switchLimbTime ?? null
          }
        }),
      []
    ),
    close: useCallback(() => setModal(null), []),
    modal
  };
};
